import { useEffect, useEffectEvent, useRef, useState } from 'react';

// Queue-a-call-until-ready latch: run() executes `action` immediately while
// `isReady` is true; otherwise it queues (`pending` is true while a queued
// call awaits the latch) and fires exactly once when isReady flips true.
// Built for one-way false→true readiness latches: queued deliberately never
// resets, so a post-flush run() always takes the immediate path, and the
// flushedRef guards effect re-runs — even a latch that misbehaves and flips
// back and forth cannot re-fire a flushed action. The flush goes through
// useEffectEvent so a queued call runs the flush-time render's action, not
// the closure captured when run() was called.
export function useRunWhenReady(
  isReady: boolean,
  action: () => void,
): { pending: boolean; run: () => void } {
  const [queued, setQueued] = useState(false);
  const flushedRef = useRef(false);
  const fire = useEffectEvent(action);
  useEffect(() => {
    if (!queued || !isReady || flushedRef.current) return;
    flushedRef.current = true;
    fire();
  }, [queued, isReady]);
  return {
    pending: queued && !isReady,
    run: () => {
      if (isReady) action();
      else setQueued(true);
    },
  };
}
